import { isHouseholdMaterial } from "../waste/wasteFlowUtils";
import { capitalizeFirstLetter } from "../../../components/utils/Date";

export const STREAM_INDUSTRIAL = "industrial";
export const STREAM_HOUSEHOLD = "household";
export const DETECTION_CHARACTERIZATION = "characterization";
export const DETECTION_MONITORING = "monitoring";

/** Même ordre que les catégories des zones d'analyse (ZoneSettingsMaterials). */
const PARENT_CATEGORY_ORDER = {
  wood: 0,
  biowaste: 1,
  cardboard: 2,
  metal: 3,
  mineral: 4,
  plastic: 5,
  plaster: 6,
  textile: 7,
  glass: 8,
  other: 9,
};

const getCategoryRank = (parentCategory) =>
  PARENT_CATEGORY_ORDER[parentCategory] ?? 99;

/**
 * Libellé de repli quand la clé i18n du referenceName est absente :
 * `hw_food_brick` devient `Food brick`.
 */
export function humanizeReferenceName(referenceName) {
  const withoutPrefix = String(referenceName || "").replace(/^hw_/, "");
  const spaced = withoutPrefix.replace(/_/g, " ").trim();
  if (!spaced) return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Traduit une matière, avec repli lisible si la clé n'existe pas encore
 * dans la langue courante (fréquent sur les matières OMR `hw_*`).
 */
export function translateMaterialName(t, referenceName) {
  if (!referenceName) return "";
  const translated = t(referenceName);
  if (translated && translated !== referenceName) return translated;
  return humanizeReferenceName(referenceName);
}

export function normalizeHex(displayColor) {
  const raw = String(displayColor || "").trim();
  if (!/^#?[0-9a-f]{3}$|^#?[0-9a-f]{6}$/i.test(raw)) return null;
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  const expanded =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;
  return `#${expanded.toUpperCase()}`;
}

function hexToRgb(displayColor) {
  const hex = normalizeHex(displayColor);
  if (!hex) return null;
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

export function formatRgb(displayColor) {
  const rgb = hexToRgb(displayColor);
  if (!rgb) return null;
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/**
 * Matières du référentiel pour un flux donné, triées catégorie puis nom.
 * Ne pas filtrer sur `detected` : ce drapeau vaut false par défaut en base
 * et exclurait la majorité du catalogue.
 */
export function getCatalogMaterials(rawMaterials, streamType, t) {
  const household = streamType === STREAM_HOUSEHOLD;

  return (Array.isArray(rawMaterials) ? rawMaterials : [])
    .filter((material) => {
      if (!material?.referenceName) return false;
      return isHouseholdMaterial(material.referenceName) === household;
    })
    .map((material) => ({
      referenceName: material.referenceName,
      parentCategory: material.parentCategory || "other",
      displayColor: normalizeHex(material.displayColor),
      recoverable: material.recoverable === true,
      // Drapeau backoffice qui pilote la liste de validation « erreurs de tri ».
      inSortingErrors: material.isActiveSortingError === true,
      name: translateMaterialName(t, material.referenceName),
      categoryLabel: capitalizeFirstLetter(
        t(material.parentCategory || "other"),
      ),
    }))
    .sort((a, b) => {
      const rankDiff =
        getCategoryRank(a.parentCategory) - getCategoryRank(b.parentCategory);
      if (rankDiff !== 0) return rankDiff;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
}

/** Options de filtre catégorie, dérivées des matières réellement présentes. */
export function getCategoryOptions(materials) {
  const byKey = new Map();
  materials.forEach((material) => {
    if (!byKey.has(material.parentCategory)) {
      byKey.set(material.parentCategory, material.categoryLabel);
    }
  });

  return [...byKey.entries()]
    .sort(([a], [b]) => getCategoryRank(a) - getCategoryRank(b))
    .map(([value, label]) => ({ value, label }));
}

export function filterCatalogMaterials(
  materials,
  { keyword, category, detection } = {},
) {
  const needle = String(keyword || "")
    .trim()
    .toLowerCase();

  return materials.filter((material) => {
    if (category && material.parentCategory !== category) return false;
    if (detection === DETECTION_MONITORING && !material.inSortingErrors) {
      return false;
    }
    if (!needle) return true;
    return [
      material.name,
      material.referenceName,
      material.categoryLabel,
      material.displayColor,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
}
